import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core'
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog'
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar'
import { NavigationEnd, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import * as Sentry from '@sentry/angular'
import { HubtypePusherApp } from 'models/hubtype-pusher-app'
import {
  IntegrationEvent,
  IntegrationReadyEvent,
} from 'models/integration-event'
import { EMPTY, Observable, of } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { UpdatePusherSettings } from 'src/app/actions/infrastructure'
import { deserialise } from 'utils/json-utils'
import { environment } from '../environments/environment'
import * as auth from './actions/auth'
import * as organization from './actions/organization'
import * as UserActions from './actions/user'
import { toggleNavbarCollapse } from './actions/user-settings.actions'
import {
  AnalyticsEvents,
  dataFromPathVariables,
  getViewNameByUrl,
} from './constants/analytics'
import { FeatureFlag } from './constants/feature-flags'
import { HubtypeOrganizationComplete } from './models/hubtype-organization'
import { HubtypeUser } from './models/hubtype-user'
import { OAuth } from './models/oauth'
import * as fromRoot from './reducers'
import { CaseList } from './reducers/desk.state'
import { CaseArchiveGuard } from './services/case-archive.guard'
import { ColorSchemeService } from './services/color-scheme.service'
import { DesktopNotifications } from './services/desktop-notifications'
import { FeedbackService } from './services/feedback.service'
import { CaseService } from './services/hubtype-api/case.service'
import { OrganizationService } from './services/hubtype-api/organization.service'
import { UserService } from './services/hubtype-api/user.service'
import { IdleGuardService } from './services/idle-guard.service'
import { IntegrationsService } from './services/integrations.service'
import { PermissionsService } from './services/permissions.service'
import { AngularComponent } from './shared/AngularComponent'
import { AwayDialogComponent } from './shared/away-dialog/away-dialog.component'
import { FeedbackComponent } from './shared/shared-brand/feedback/feedback.component'
import { Analytics } from './utils/analytics'

declare global {
  interface Window {
    userGuiding: any
  }
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent extends AngularComponent implements OnInit {
  private userGuideIdentified = false

  public $hasCaseArchiveAccess: Observable<boolean>
  public auth: any = {}

  public dialogRef: MatDialogRef<any>
  public hasAnalyticsAccess = this.permissionsService.hasAnalyticsAccess
  public hasAnalyticsConversationsAccess =
    this.permissionsService.hasAnalyticsConversationsAccess
  public hasOrgSettingsAccess = this.permissionsService.hasOrgSettingsAccess
  public hasQaEvaluatorAccess = this.permissionsService.hasQaEvaluatorAccess
  public hasAiKnowledgeAccess = false
  public hasCampaignsAccess = false
  public hasCampaignsV2Access = false
  integrationsListener: Observable<IntegrationEvent>
  public isAgent = false
  isCollapse = true
  public isFullApp = !environment.minimalVersion
  public me: HubtypeUser
  public organization: HubtypeOrganizationComplete
  public splash = true

  // TODO Remove this once the inbox-v2 is the default
  public isInboxLinkActive = signal(false)
  public isArchiveLinkActive = signal(false)

  constructor(
    private router: Router,
    private store: Store<fromRoot.State>,
    private dialog: MatDialog,
    private ref: ChangeDetectorRef,
    public feedbackBar: MatSnackBar,
    private feedbackService: FeedbackService,
    private desktopNotification: DesktopNotifications,
    private orgService: OrganizationService,
    private userService: UserService,
    private idleGuardService: IdleGuardService,
    private colorSchemeService: ColorSchemeService,
    private integrationsService: IntegrationsService,
    private caseService: CaseService,
    private caseArchiveGuard: CaseArchiveGuard,
    private permissionsService: PermissionsService
  ) {
    super()
    this.colorSchemeService.load()
  }

  _buildDialog() {
    this.dialogRef = this.dialog.open(AwayDialogComponent, {
      width: '460px',
    })
    this.dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((keepWorking: boolean) => {
        if (!keepWorking) {
          this.userService.setAvailable().subscribe()
        }
      })
  }

  loadAuth() {
    const queryParams = new URLSearchParams(window.location.search)
    const access_token = queryParams.get('access_token')
    const refresh_token = queryParams.get('refresh_token')
    if (access_token && refresh_token) {
      this.store.dispatch(
        new auth.LoginAction(
          deserialise(
            { access_token, refresh_token, token_type: 'Bearer' },
            OAuth
          )
        )
      )
    }

    const auths = this.store
      .select(fromRoot.getAuthState)
      .pipe(filter(auth => !auth || !auth.oauth))

    this.subscribeUntilDestroy(auths, auth => {
      this.me = null
      this.splash = false
      this.ref.markForCheck()
    })
  }

  loadCases() {
    //Initial cases loading
    this.store
      .select(fromRoot.isLoggedIn)
      .pipe(filter(isLogged => isLogged === true))
      .subscribe(() => {
        this.store
          .select(fromRoot.getPusherConnectedStatus)
          .pipe(first(Boolean))
          .subscribe(() => {
            this.caseService.loadCasesInParallel(CaseList.ATTENDING)
            this.caseService.loadCasesInParallel(CaseList.IDLE)
            this.caseService.loadCasesInParallel(CaseList.WAITING)
          })
      })
  }

  loadOrganization() {
    const hubtypeOrganizationCompletes = this.store
      .select(fromRoot.getAuthState)
      .pipe(
        filter(auth => Boolean(auth && auth.oauth && !auth.error && !this.me)),
        switchMap((auth: any) => {
          this.ref.markForCheck()
          return this.userService.getMe(true).pipe(
            catchError(err => {
              this.logout('An error ocurred, please try again later')
              return EMPTY
            })
          )
        }),
        switchMap((me: HubtypeUser) => {
          if (environment.sentryURL) {
            Sentry.getCurrentScope().setUser({ id: me.id })
          }
          this.ref.markForCheck()
          this.splash = true
          if (me.isOffline) {
            return this.userService.setOnline(me).pipe(
              catchError(err => {
                // Error 409 occurs when the user was not in offline status
                if (err?.status === 409) {
                  return of(me)
                }
                this.logout('An error occurred setting online the user')
              })
            )
          }
          return of(me)
        }),
        switchMap((me: HubtypeUser) => {
          setTimeout(() => Analytics.user(me.id, me), 300)
          if (this.desktopNotification.isPermissionDefaul()) {
            this.desktopNotification.allowNotifications().then(r => {
              if (r === 'denied') {
                me.notifications_new_case = false
                me.notifications_new_message = false
                me.notifications_assigned_case = false
              }
            })
          }
          this.store.dispatch(new auth.SetMeAction(me))
          if (me.isAway) {
            this._buildDialog()
          }
          this.ref.markForCheck()
          this.splash = true
          return this.orgService.getAndAssembleHubtypeOrganization()
        })
      )

    this.subscribeUntilDestroy(
      hubtypeOrganizationCompletes,
      (org: HubtypeOrganizationComplete) => {
        this.ref.markForCheck()
        if (org) {
          this.organization = org
          this.userService.setTranslationsAccess(this.organization.simplified)

          this.hasCampaignsAccess =
            this.organization.simplified.hasAccessTo(FeatureFlag.CAMPAIGNS) &&
            (this.me.is_admin || this.me.campaigns_exceptional_access)

          this.hasCampaignsV2Access =
            this.organization.simplified.hasAccessTo(
              FeatureFlag.CAMPAIGNS_V2_ENABLED
            ) &&
            (this.me.is_admin || this.me.campaigns_exceptional_access)

          this.isAgent = this.me.is_agent

          this.hasAiKnowledgeAccess = this.me.is_admin

          Analytics.group(this.organization.id, {
            name: this.organization.name,
            contract_type: this.organization.contract_type,
          })
        }
        this.store.dispatch(UserActions.setUsers({ users: org.users }))
        this.store.dispatch(
          new organization.SetOrganizationAction({
            organization: org,
            meId: this.me ? (this.me.id as string) : '',
          })
        )
        this.store.dispatch(
          new UpdatePusherSettings(HubtypePusherApp.fromOrganization(org))
        )
        this.splash = false
      },
      err => {
        console.log(err)
        if (err.status === 403) {
          this.logout('You do not have permission to login')
        } else {
          this.logout('Server not responding, please try again later')
        }
      }
    )
  }

  logout(error_msg?: string) {
    this.userService
      .setOffline()
      .pipe(
        finalize(() => {
          this.store.dispatch(new auth.LogoutAction())
          this.ref.markForCheck()
          if (error_msg) {
            this.router.navigate(['/sign-in', { error: error_msg }])
          } else {
            this.router.navigate(['/sign-in'])
          }
        })
      )
      .subscribe()
  }

  ngOnInit() {
    this.idleGuardService.handleUserIdle()
    let previousUrl = ''
    const changed = this.router.events.pipe(
      distinctUntilChanged((previous: any, current: any) => {
        if (current instanceof NavigationEnd) {
          previousUrl = previous.url
          return previous.url === current.url
        }
        return true
      }),
      // TODO Remove this once the inbox-v2 is the default
      tap(({ url }: NavigationEnd) => {
        this.isInboxLinkActive.set(url.includes('/desk/inbox'))
        this.isArchiveLinkActive.set(url.includes('/desk/archive'))
      })
    )
    this.subscribeUntilDestroy(changed, (x: any) => {
      const url = this.router.url
      const name = getViewNameByUrl(url)
      const data = dataFromPathVariables(url) || {}
      if (this.me) {
        setTimeout(() => Analytics.user(this.me.id, this.me), 300)
      }
      Analytics.view(name, { data, ...{ referrer: previousUrl } })
    })

    this.feedbackService.observe(
      feedback => {
        if (feedback.isError() && feedback.error) {
          console.error(feedback.error)
        }
        this.feedbackBar.openFromComponent(FeedbackComponent, {
          ...feedback.config,
          data: feedback,
        })
      },
      err => console.error(err)
    )

    this.store
      .select(fromRoot.getMeState)
      .pipe(
        filter(me => Boolean(me)),
        tap(me => {
          this.identifyUserGuiding(me)
        })
      )
      .subscribe(me => {
        this.me = me
      })

    this.store
      .select(fromRoot.getMeState)
      .pipe(first(Boolean))
      .subscribe(me => {
        this.dispatchLocalAIAvailability(me)
        this.trackInboxVersionInitialization(me)
      })

    this.store
      .select(fromRoot.isNavBarCollapsed)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(isCollapse => {
        this.isCollapse = isCollapse
      })

    this.loadAuth()
    this.loadIntegrations()
    this.loadOrganization()
    this.loadCases()
    this.setMenuVisibility()
  }

  setMenuVisibility() {
    const organization$ = this.store.select(fromRoot.getOrganization)
    organization$
      .pipe(
        filter(organization => Boolean(organization)),
        first()
      )
      .subscribe(org => {
        this.$hasCaseArchiveAccess = this.caseArchiveGuard
          .canActivate()
          .pipe(first())
      })
  }

  toggleAway() {
    if (!this.me.isAway) {
      this.userService.setAway().subscribe()
    }
    this._buildDialog()
  }

  toogleCollapse() {
    Analytics.event(AnalyticsEvents.NAVBAR_COLLAPSE_UNCOLLAPSE, {
      collapse: !this.isCollapse,
    })
    this.store.dispatch(toggleNavbarCollapse())
  }

  navigateToReactApp(path: string) {
    window.location.href = path + '?v=' + Date.now()
  }

  private identifyUserGuiding(user: HubtypeUser) {
    if (!this.userGuideIdentified && user && window.userGuiding) {
      console.info('Identifying user for UserGuiding')
      window.userGuiding.identify(user.id, {
        role: user.role,
        organizationId: user.organization_id,
        hasAnalyticsAccess: user.is_admin || user.is_manager,
        hasTranslationService: user.has_translation_service,
      })
    }
  }

  private loadIntegrations() {
    if (this.integrationsService.isUsingIntegrations()) {
      this.integrationsService.sendEvent(new IntegrationReadyEvent())

      this.integrationsService
        .onEventReceived()
        .pipe(
          filter(ev => ev.type === 'init'),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe(ev => {
          const caseId = ev.payload?.caseId
          let resourceId: string = ev.payload?.resourceId // /resource/{namespace_id}/hubtypechat

          if (!caseId) {
            console.error('Integration invoked without a `caseId` present')
            return
          }
          if (!resourceId) {
            console.error('Integration invoked without a `resourceId` present')
            return
          }
          resourceId = resourceId.replace('resource', 'lcc') + '/'
          console.log('Integration ready')

          setTimeout(() => {
            this.router.navigate(['desk', 'inbox', caseId])
          }, 500)
        })
    }
  }

  // This is to collect stats about the local AI availability for each user
  // TODO Remove this once we have enough insights
  private async dispatchLocalAIAvailability(me: HubtypeUser) {
    if (!me) return

    let isSummarizerAPIAvailable = false
    let isTranslateAPIAvailable = false
    let isLanguageDetectorAPIAvailable = false
    let hadError = false
    let sourceLanguage = me?.translation_settings?.primary_language
    let targetLanguage =
      me?.translation_settings?.always_translate_languages?.[0]

    try {
      if (
        !sourceLanguage ||
        !targetLanguage ||
        sourceLanguage === targetLanguage
      ) {
        // If no language config available, we use these languages just to know the client capabilities
        sourceLanguage = 'en'
        targetLanguage = 'es'
      }

      // Run all API availability checks in parallel
      const results = await Promise.allSettled([
        'Summarizer' in self
          ? (self as any).Summarizer.availability()
          : Promise.resolve(false),
        'LanguageDetector' in self
          ? (self as any).LanguageDetector.availability()
          : Promise.resolve(false),
        'Translator' in self
          ? (self as any).Translator.availability({
              sourceLanguage,
              targetLanguage,
            })
          : Promise.resolve(false),
      ])

      isSummarizerAPIAvailable =
        results[0].status === 'fulfilled' && results[0].value
      isLanguageDetectorAPIAvailable =
        results[1].status === 'fulfilled' && results[1].value
      isTranslateAPIAvailable =
        results[2].status === 'fulfilled' && results[2].value
    } catch (_) {
      hadError = true
    } finally {
      Analytics.event(AnalyticsEvents.LOCAL_AI_AVAILABILITY, {
        userId: me.id,
        userLanguage: sourceLanguage,
        targetLanguage,
        isSummarizerAPIAvailable,
        isTranslateAPIAvailable,
        isLanguageDetectorAPIAvailable,
        hadError,
      })
    }
  }

  private trackInboxVersionInitialization(me: HubtypeUser) {
    this.store
      .select(fromRoot.selectUseInboxV2)
      .pipe(first())
      .subscribe(useInboxV2 => {
        Analytics.event(AnalyticsEvents.INBOX_VERSION_INITIALIZED, {
          userId: me.id,
          inboxVersion: useInboxV2 ? 'v2' : 'v1',
        })
      })
  }
}
